import React from 'react';
import { isKeyHotkey } from 'is-hotkey';
import { Editor, Element, Node, Range, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';
import Hotkeys from 'slate-hotkeys';

import Block from './components/Block';
import type AhaEditor from '../../AhaEditor';
import { getCurrentListItems } from '../ListPlugin/utils';

import BlockUtils, {
  DEFAULT_BLOCK_TYPE,
  type BlockFormatOptions,
} from './BlockUtils';
import type { ComponentPlugin, EditorPlugin } from '../../types/aha-editor';

const DEFAULT_OPTIONS = {
  type: DEFAULT_BLOCK_TYPE,
  isMultiline: false,
  clearFormattingAtEnd: true,
  exitOnBackspace: false,
  allowTab: false,
  internalSoftBreak: false,
  matchIndent: false,
  hasCustomComponent: false,
  hasAnchor: false,
  dependencies: [],
};

const isEnter = isKeyHotkey('enter');
const isNonShiftEnter = (event: KeyboardEvent) =>
  !event.shiftKey && isEnter(event);
const isShiftEnter = isKeyHotkey('shift+enter');
const isTab = isKeyHotkey('tab');

// Generate a plugin for a given block type.
const BlockFormatPluginFactory = (
  options: BlockFormatOptions
): EditorPlugin => {
  options = { ...DEFAULT_OPTIONS, title: options.type, ...options };

  BlockUtils.blockOptions[options.type] = options;

  const isHardBreak = options.isMultiline ? isShiftEnter : isNonShiftEnter;
  const isSoftBreak = options.isMultiline ? isNonShiftEnter : isShiftEnter;

  /**
   * When inputting text using input method editors (IME) for
   * languages that use character composition, such as Chinese,
   * Japanese or Korean, we want to swallow the different events
   * caused by user interactions with the IME.
   *
   * To achieve this, we have to keep track when the user is in the
   * middle of an IME input and ignore events received during that time.
   */

  const onKeyDown = (
    event: KeyboardEvent,
    ahaEditor: AhaEditor
  ): boolean | undefined => {
    const { editor } = ahaEditor;
    if (isEnter(event) && ReactEditor.isComposing(editor)) return true;

    if (!editor.selection) return;

    if (!BlockUtils.selectionIsInBlock(editor, options.type)) return;

    if (handleTab(event, editor)) {
      return true;
    }

    if (handleBlockEscape(event, editor)) {
      return true;
    }

    const parent = Node.parent(editor, editor.selection.anchor.path);

    // Check if the selection is inside a void element.
    if (
      editor.selection &&
      Range.isCollapsed(editor.selection) &&
      Element.isElement(parent) &&
      Editor.isVoid(editor, parent)
    ) {
      return;
    }

    if (handleBlockExit(event, editor)) {
      return true;
    }

    if (!options.skipHandleBlockSplits && handleBlockSplits(event, editor)) {
      return true;
    }
  };

  const handleTab = (event: KeyboardEvent, editor: Editor): boolean => {
    if (!options.allowTab) return false;
    if (!isTab(event)) return false;
    event.preventDefault();
    Editor.insertText(editor, '\t');

    return true;
  };

  const handleBlockExit = (event: KeyboardEvent, editor: Editor) => {
    if (!options.exitOnBackspace) return;
    if (
      Hotkeys.isDeleteBackward(event) &&
      editor.selection &&
      Range.isCollapsed(editor.selection) &&
      BlockUtils.selectionIsAtStartOfBlockType(editor, options.type)
    ) {
      BlockUtils.resetBlock(editor);
      event.preventDefault();
      return true;
    }
  };

  const handleBlockSplits = (event: KeyboardEvent, editor: Editor): boolean => {
    if (isHardBreak(event)) {
      // If we are in a list, defer to the list handling.
      if (getCurrentListItems(editor).length > 0) return false;

      const atEndOfblock =
        editor.selection &&
        Range.isCollapsed(editor.selection) &&
        Editor.isEnd(
          editor,
          Editor.end(editor, editor.selection.anchor.path),
          editor.selection.anchor
        );
      if (editor.selection && !atEndOfblock && options.internalSoftBreak) {
        editor.insertText('\n');
      } else {
        Transforms.splitNodes(editor, { always: true });

        if (options.clearFormattingAtEnd && atEndOfblock) {
          BlockUtils.resetBlock(editor);
        }
      }

      event.preventDefault();
      return true;
    }

    if (isSoftBreak(event)) {
      let indent = '';
      if (options.matchIndent) {
        // We are going to insert soft tabs to make the next have the same indent.
        indent = BlockUtils.currentLineIndent(editor);
      }
      editor.insertText(`\n${indent}`);

      event.preventDefault();
      return true;
    }

    return false;
  };

  // For multi-line blocks make sure it is possible to arrow up or down into
  // a block above or below, creating one if necessary.
  const handleBlockEscape = (event: KeyboardEvent, editor: Editor): boolean => {
    const { selection } = editor;
    if (!options.isMultiline) return false;
    if (!selection) return false;
    if (!Range.isCollapsed(selection)) return false;

    if (
      event.key === 'ArrowUp' &&
      Editor.isStart(
        editor,
        Editor.start(editor, selection.anchor.path),
        selection.anchor
      )
    ) {
      BlockUtils.escapeBlock(editor, options.type, 'above');
      return true;
    } else if (
      event.key === 'ArrowDown' &&
      Editor.isEnd(
        editor,
        Editor.end(editor, selection.anchor.path),
        selection.anchor
      )
    ) {
      BlockUtils.escapeBlock(editor, options.type, 'below');
      return true;
    }

    return false;
  };

  const components: ComponentPlugin[] = [];
  const tag = options.tag;
  if (!options.hasCustomComponent && tag) {
    components.push({
      type: options.type,
      component: ({ attributes, children, element }) => {
        return (
          <Block
            {...options}
            attributes={attributes}
            tag={tag}
            element={element}
          >
            {children}
          </Block>
        );
      },
    });
  }

  return {
    name: `${options.type} Block format plugin`,
    dependencies: options.dependencies,
    components,
    handlers: {
      onKeyDown,
    },
  };
};

export default BlockFormatPluginFactory;
